import {Injectable} from '@angular/core';
import {
  DEAL_PAYROLL_STATUS, DealPayrollBreakdown, DealPayrollTaskLine, isDealPayrollPayable,
  isFinitePayrollNumber
} from '../../../models/deal-payroll.model';
import {
  dealPayrollGradeKey, dealPayrollProfileKey
} from '../../../models/deal-payroll-presentation';
import {CurrencyAmount} from '../../../models/payroll-currency.model';
import {
  ACCESS_DENIED_DETAIL_ERROR_KEY, GENERIC_DETAIL_ERROR_KEY, REMOTE_DETAIL_SOURCE
} from './deal-payroll-detail.loader.constants';
import {
  EMPTY_COUNT, EMPTY_VALUE, FINAL_CURRENCIES_LABEL_KEY, FINAL_SALARY_LABEL_KEY,
  FIRST_INDEX,
  MINIMUM_PLANNED_HOURS,
  PREVIEW_CURRENCIES_LABEL_KEY, PREVIEW_SALARY_LABEL_KEY, PREVIEW_WARNING_KEY
} from './deal-payroll-detail.view.constants';
import {
  DealPayrollDetailIssueView, DealPayrollDetailPresentationContext,
  DealPayrollDetailTaskGroup, DealPayrollDetailTaskView, DealPayrollModifierView
} from './deal-payroll-detail.view.types';
import {
  dealPayrollFinalAmountViews, dealPayrollModifierViews, dealPayrollTaskGroups,
  dealPayrollVisibleIssueViews
} from './deal-payroll-detail.view';
import {componentSummary, DealPayrollComponentSummary} from './deal-payroll-detail.components';

const PERCENT_SCALE = 100;

@Injectable()
export class DealPayrollDetailViewState {
  breakdown: DealPayrollBreakdown;
  loading = false;
  loadFailed = false;
  errorKey = GENERIC_DETAIL_ERROR_KEY;
  previewWarningKey = PREVIEW_WARNING_KEY;
  modifierViews: DealPayrollModifierView[] = [];
  modifiersAvailable = false;
  modifiersFromReport = false;
  issueViews: DealPayrollDetailIssueView[] = [];
  visibleIssues: DealPayrollDetailIssueView[] = [];
  taskGroups: DealPayrollDetailTaskGroup[] = [];
  taskLineViews: DealPayrollDetailTaskView[] = [];
  finalAmountViews: CurrencyAmount[] = [];
  payrollProfileLabelKey = EMPTY_VALUE;
  gradeLabelKey = EMPTY_VALUE;
  openGroupKey: string = null;
  componentTotals: DealPayrollComponentSummary | null = null;

  get plannedPercent(): number | null {
    const value = this.breakdown && this.breakdown.plannedWeight;
    return isFinitePayrollNumber(value) ? value * PERCENT_SCALE : null;
  }

  get finalSalaryLabelKey(): string {
    return this.isIncomplete ? PREVIEW_SALARY_LABEL_KEY : FINAL_SALARY_LABEL_KEY;
  }

  get finalSalaryCurrenciesLabelKey(): string {
    return this.isIncomplete ? PREVIEW_CURRENCIES_LABEL_KEY : FINAL_CURRENCIES_LABEL_KEY;
  }

  private get isIncomplete(): boolean {
    return !!this.breakdown && this.breakdown.status === DEAL_PAYROLL_STATUS.INCOMPLETE;
  }

  get monthlyBase(): number | null {
    const value = this.breakdown && this.breakdown.baseMonthlySalary;
    return isFinitePayrollNumber(value) ? value : null;
  }

  get monthlyNorm(): number | null {
    const value = this.breakdown && this.breakdown.workingHoursPerMonth;
    return isFinitePayrollNumber(value) && value > MINIMUM_PLANNED_HOURS ? value : null;
  }

  get baseHourRate(): number | null {
    const value = this.breakdown && this.breakdown.baseHourRate;
    return isFinitePayrollNumber(value) ? value : null;
  }

  get employeePlannedHourRate(): number | null {
    const value = this.breakdown;
    return value ? this.gradeHourRate(value.plannedHourRate, value.gradeCoefficient) : null;
  }

  get employeeActualHourRate(): number | null {
    const value = this.breakdown;
    return value ? this.gradeHourRate(value.actualHourRate, value.gradeCoefficient) : null;
  }

  private taskPlannedHourRate(task: DealPayrollTaskLine): number | null {
    return this.breakdown && task
      ? this.gradeHourRate(this.breakdown.plannedHourRate, task.gradeCoefficient) : null;
  }

  private taskActualHourRate(task: DealPayrollTaskLine): number | null {
    if (!this.breakdown || !task) {
      return null;
    }
    const rate = this.isTaskMissingPlan(task) ? this.breakdown.baseHourRate : this.breakdown.actualHourRate;
    return this.gradeHourRate(rate, task.gradeCoefficient);
  }

  private gradeHourRate(rate: number, coefficient: number): number | null {
    if (!isFinitePayrollNumber(rate) || rate < EMPTY_COUNT
      || !isFinitePayrollNumber(coefficient) || coefficient <= EMPTY_COUNT) {
      return null;
    }
    const value = rate * coefficient;
    return isFinitePayrollNumber(value) ? value : null;
  }

  isDealPayrollComplete(value: DealPayrollBreakdown): boolean {
    return !!value && isDealPayrollPayable(value.status);
  }

  isDealPayrollPreview(value: DealPayrollBreakdown): boolean {
    return !!value && value.status === DEAL_PAYROLL_STATUS.INCOMPLETE
      && this.finalAmountViews.length > EMPTY_COUNT;
  }

  isTaskMissingPlan(task: DealPayrollTaskLine): boolean {
    return !!task && (!isFinitePayrollNumber(task.plannedHours) || task.plannedHours <= MINIMUM_PLANNED_HOURS);
  }

  hasTasksWithoutPlan(group: DealPayrollDetailTaskGroup): boolean {
    return group.rows.some((task: DealPayrollDetailTaskView) => this.isTaskMissingPlan(task));
  }

  hasDealPayrollAmount(value: number): boolean {
    return isFinitePayrollNumber(value);
  }

  usesOnePayrollCurrency(): boolean {
    return !!this.breakdown && this.breakdown.currency === this.breakdown.salaryCurrency;
  }

  hasDealPayrollPolicyAudit(): boolean {
    const value = this.breakdown;
    return !!value && (!!value.gradeCode
      || isFinitePayrollNumber(value.actualHourRate));
  }

  showDealPayrollNoTasks(): boolean {
    return this.isDealPayrollComplete(this.breakdown) && this.breakdown.taskLineCount === EMPTY_COUNT
      && !!this.breakdown.taskLines && !this.breakdown.taskLines.length;
  }

  canRetry(): boolean {
    return this.errorKey !== ACCESS_DENIED_DETAIL_ERROR_KEY;
  }

  startLoading(): void {
    this.loading = true;
    this.loadFailed = false;
    this.errorKey = GENERIC_DETAIL_ERROR_KEY;
    this.clearBreakdownView();
  }

  showError(errorKey: string): void {
    this.loading = false;
    this.loadFailed = true;
    this.errorKey = errorKey;
  }

  rejectSnapshot(errorKey: string): void {
    this.loading = false;
    this.loadFailed = true;
    this.errorKey = errorKey;
    this.clearBreakdownView();
  }

  useBreakdown(
    value: DealPayrollBreakdown,
    context: DealPayrollDetailPresentationContext
  ): void {
    this.breakdown = value;
    this.applyLabels(value);
    this.applyModifiers(context);
    this.applyAmountsAndIssues(value);
    this.applyTasks(value);
    this.loading = false;
    this.loadFailed = false;
  }

  private applyLabels(value: DealPayrollBreakdown): void {
    this.payrollProfileLabelKey = dealPayrollProfileKey(value.payrollProfile);
    this.gradeLabelKey = dealPayrollGradeKey(value.gradeCode);
  }

  private applyModifiers(context: DealPayrollDetailPresentationContext): void {
    this.modifiersAvailable = !!context.calculation
      && context.calculation.length > EMPTY_COUNT;
    this.modifierViews = this.modifiersAvailable
      ? dealPayrollModifierViews(context.calculation, this.breakdown.salaryCurrency) : [];
    this.modifiersFromReport = this.modifiersAvailable
      && (context.source === REMOTE_DETAIL_SOURCE || !context.calculationTrusted);
  }

  private applyAmountsAndIssues(value: DealPayrollBreakdown): void {
    this.finalAmountViews = value.status === DEAL_PAYROLL_STATUS.DISABLED
      ? [] : dealPayrollFinalAmountViews(value);
    this.issueViews = dealPayrollVisibleIssueViews(value);
    this.visibleIssues = this.issueViews;
  }

  private applyTasks(value: DealPayrollBreakdown): void {
    this.componentTotals = componentSummary(value);
    this.taskGroups = dealPayrollTaskGroups(value);
    this.openGroupKey = this.taskGroups.length
      ? this.taskGroups[FIRST_INDEX].key : null;
    this.taskLineViews = this.flattenTaskRows();
    this.taskLineViews.forEach(task => {
      task.employeePlannedHourRate = this.taskPlannedHourRate(task);
      task.employeeActualHourRate = this.taskActualHourRate(task);
      task.employeePayableHours = task.dailyPayableHours
        ? Object.keys(task.dailyPayableHours).reduce((hours, day) => hours + task.dailyPayableHours[day], FIRST_INDEX)
        : null;
    });
  }

  private flattenTaskRows(): DealPayrollDetailTaskView[] {
    return this.taskGroups.reduce(
      (rows: DealPayrollDetailTaskView[], group: DealPayrollDetailTaskGroup) =>
        rows.concat(group.rows), []);
  }

  private clearBreakdownView(): void {
    this.componentTotals = null;
    this.breakdown = undefined;
    this.issueViews = [];
    this.visibleIssues = [];
    this.taskGroups = [];
    this.openGroupKey = null;
    this.taskLineViews = [];
    this.finalAmountViews = [];
    this.modifierViews = [];
    this.modifiersAvailable = false;
    this.modifiersFromReport = false;
    this.payrollProfileLabelKey = EMPTY_VALUE;
    this.gradeLabelKey = EMPTY_VALUE;
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/my-working-report/deal-payroll-detail/deal-payroll-detail.view-state.ts